import { isEmpty } from '@ember/utils'
import { capitalize } from '@ember/string'
import { startCase } from 'lodash'
import type { NormalizedInterval } from 'date-fns'
import type { SchoolScope, StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'

export interface CertificateEventsResponse {
  data: {
    id: number
    school_id: number
    student_id: number
    created_at: string // ISO8601 "2024-12-22T23:36:19.000+00:00"
    product: 'reading' | 'rex' | 'fast_phonics' | 'maths' | 'writing_legends'
    precinct: string // 'lessons' | 'spelling' | 'none' | 'mental_minute', potentially others
    lesson?: Nullable<number>
    event_details: {
      url?: string
      // eslint-disable-next-line customPlugin/prefer-american-color
      colour?: string // not present for mental minute
      map_number?: Nullable<string> // not present for rex, FP
      category?: string // mental minute
      stage?: string // mental minute
      stage_type?: string // mental minute
      date?: string
      score_percentage?: string // re, mathseeds
      locale?: string // mathseeds mental minute
      [key: string]: unknown
    }
    [key: string]: unknown
  }[]
}

export interface CertificateEventsData {
  id: string
  student_id: string
  created_at: Date
  product: string
  url: string
  description: string
  precinct: string
  legacy_cert_details: {
    color?: string
    lesson?: Nullable<number>
    map?: number
    score?: number
    date?: string
    stage?: string
    stage_type?: string
  }
}

export async function certificateEvents(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductRex | LegacyProductFp | LegacyProductMs | LegacyProductWl,
  scope: SchoolScope | StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<CertificateEventsData[]>> {
  if (isEmpty(scope.ids)) return null

  const url = this.buildUrl('events/certificates', product, scope)
  const query = this.buildQuery(scope, period, { limit: 1000 })

  const response = await this.fetchData<CertificateEventsResponse>(url, query)
  if (!response?.data) return null

  return response.data.map((c) => {
    // eslint-disable-next-line customPlugin/prefer-american-color
    const color = c.event_details.colour ? capitalize(c.event_details.colour) : ''

    let description = color
    if (
      c.product === 'reading' &&
      c.event_details.map_number &&
      (c.precinct === 'lessons' || c.precinct === 'spelling')
    ) {
      // RE description is like	"My Gold certificate (Map 1)"
      description = `My ${color} certificate (Map ${c.event_details.map_number})`
    } else if (c.precinct === 'mental_minute' && c.event_details.category && c.event_details.stage) {
      description = `Mental Minute > ${startCase(c.event_details.category)} > ${startCase(c.event_details.stage)}`
    }
    return {
      id: c.id.toString(),
      student_id: c.student_id.toString(),
      created_at: new Date(c.created_at),
      product: c.product,
      url: c.event_details.url || '',
      precinct: c.precinct,
      description,
      legacy_cert_details: {
        // eslint-disable-next-line customPlugin/prefer-american-color
        color: c.event_details.colour,
        lesson: c.lesson,
        map: c.event_details.map_number ? parseInt(c.event_details.map_number) : undefined,
        score: c.event_details.score_percentage ? parseInt(c.event_details.score_percentage) : undefined,
        stage: c.event_details.stage,
        stage_type: c.event_details.stage_type,
        date: c.event_details.date,
      },
    }
  })
}
